import React from "react";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

export const PRODUCT_EXTRAS_SLUGS = [
    "farm-equipments",
    "home-decor",
    "cement",
    "paints",
    "building-materials",
    "jewellery",
    "real-estate",
];

export const hasProductExtras = (slug) => PRODUCT_EXTRAS_SLUGS.includes(slug);

/**
 * Extra category-specific fields that pile on TOP of the standard product
 * flow (Brand / Condition / Quantity / Photos / Price). Values persist on
 * `listing.meta` (flat key/value), exactly like JobsMetaForm / ServicesMetaForm.
 */
export default function ProductMetaExtras({ category, meta = {}, onChange }) {
    const set = (key, value) => onChange({ ...meta, [key]: value });

    if (category === "farm-equipments") {
        return (
            <section data-testid="farm-eq-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Equipment details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Buyers compare on machine type, power, fuel and hours used.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Machine type *</label>
                        <Select value={meta.machine_type || ""} onValueChange={(v) => set("machine_type", v)}>
                            <SelectTrigger data-testid="meta-machine-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Tractor", "Harvester", "Power Tiller", "Rotavator", "Plough", "Cultivator", "Seed Drill", "Threshing Machine", "Sprayer", "Irrigation Pump", "Mini-Truck / Pickup", "Other"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Power (HP)</label>
                        <Input
                            data-testid="meta-power-hp"
                            inputMode="numeric"
                            value={meta.power_hp ?? ""}
                            onChange={(e) => set("power_hp", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="e.g. 45"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Fuel</label>
                        <Select value={meta.fuel_type || ""} onValueChange={(v) => set("fuel_type", v)}>
                            <SelectTrigger data-testid="meta-fuel-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Diesel", "Petrol", "Electric", "CNG", "Manual"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Year of manufacture</label>
                        <Input
                            data-testid="meta-year"
                            inputMode="numeric"
                            value={meta.year ?? ""}
                            onChange={(e) => set("year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="e.g. 2020"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Hours used</label>
                        <Input
                            data-testid="meta-hours-used"
                            inputMode="numeric"
                            value={meta.hours_used ?? ""}
                            onChange={(e) => set("hours_used", e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="e.g. 1500"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Drive</label>
                        <Select value={meta.drive || ""} onValueChange={(v) => set("drive", v)}>
                            <SelectTrigger data-testid="meta-drive" className="mt-1"><SelectValue placeholder="2WD / 4WD" /></SelectTrigger>
                            <SelectContent>
                                {["2WD", "4WD", "N/A"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Attachments / implements included</label>
                    <Textarea
                        data-testid="meta-attachments"
                        rows={2}
                        value={meta.attachments || ""}
                        onChange={(e) => set("attachments", e.target.value)}
                        maxLength={400}
                        placeholder="e.g. Plough, Rotavator, Trolley, Spray pump…"
                        className="mt-1"
                    />
                </div>
            </section>
        );
    }

    if (category === "home-decor") {
        return (
            <section data-testid="home-decor-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Decor details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Help buyers visualise the piece in their home.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Item type</label>
                        <Select value={meta.decor_type || ""} onValueChange={(v) => set("decor_type", v)}>
                            <SelectTrigger data-testid="meta-decor-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Wall Art / Paintings", "Lighting / Lamps", "Rugs & Carpets", "Curtains & Drapes", "Cushions & Throws", "Vases & Planters", "Mirrors", "Clocks", "Statues & Figurines", "Candles & Diffusers", "Other"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Style</label>
                        <Select value={meta.style || ""} onValueChange={(v) => set("style", v)}>
                            <SelectTrigger data-testid="meta-style" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Modern", "Traditional / Ethnic", "Bohemian", "Minimalist", "Industrial", "Scandinavian", "Rustic", "Art Deco", "Mid-Century", "Eclectic"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Room</label>
                        <Select value={meta.room || ""} onValueChange={(v) => set("room", v)}>
                            <SelectTrigger data-testid="meta-room" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Living Room", "Bedroom", "Kitchen", "Bathroom", "Dining", "Office / Study", "Outdoor / Balcony", "Any"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Material</label>
                        <Input
                            data-testid="meta-material"
                            value={meta.material || ""}
                            onChange={(e) => set("material", e.target.value)}
                            placeholder="e.g. Sheesham wood, Brass, Cotton…"
                            maxLength={80}
                            className="mt-1"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Dimensions <span className="text-aduku-navy/50 font-normal">(L × W × H)</span></label>
                    <Input
                        data-testid="meta-dimensions"
                        value={meta.dimensions || ""}
                        onChange={(e) => set("dimensions", e.target.value)}
                        placeholder="e.g. 60 × 40 × 5 cm"
                        maxLength={80}
                        className="mt-1"
                    />
                </div>
                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Set includes</label>
                    <Input
                        data-testid="meta-set-includes"
                        value={meta.set_includes || ""}
                        onChange={(e) => set("set_includes", e.target.value)}
                        placeholder="e.g. Set of 3, includes hanging hardware"
                        maxLength={200}
                        className="mt-1"
                    />
                </div>
            </section>
        );
    }

    if (category === "cement") {
        return (
            <section data-testid="cement-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Cement specifications</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Builders and contractors filter on grade, bag size and minimum order.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Cement grade *</label>
                        <Select value={meta.grade || ""} onValueChange={(v) => set("grade", v)}>
                            <SelectTrigger data-testid="meta-grade" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["OPC 33", "OPC 43", "OPC 53", "PPC (Portland Pozzolana)", "PSC (Portland Slag)", "Rapid Hardening", "White Cement", "Sulphate Resistant"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Bag size (kg)</label>
                        <Select value={meta.bag_size_kg || ""} onValueChange={(v) => set("bag_size_kg", v)}>
                            <SelectTrigger data-testid="meta-bag-size" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["25", "40", "50", "Bulk (no bag)"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Min order (bags / tons)</label>
                        <Input
                            data-testid="meta-min-order-bags"
                            inputMode="numeric"
                            value={meta.min_order_bags ?? ""}
                            onChange={(e) => set("min_order_bags", e.target.value.replace(/\D/g, "").slice(0, 5))}
                            placeholder="e.g. 50"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">ISI / BIS certified?</label>
                        <Select value={meta.isi_certified || ""} onValueChange={(v) => set("isi_certified", v)}>
                            <SelectTrigger data-testid="meta-isi" className="mt-1"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                            <SelectContent>
                                {["Yes — ISI / BIS certified", "No"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-aduku-navy">Bulk delivery available</div>
                        <div className="text-xs text-aduku-navy/55">Truckload delivery direct to construction site.</div>
                    </div>
                    <Switch
                        data-testid="meta-bulk-delivery"
                        checked={!!meta.bulk_delivery}
                        onCheckedChange={(v) => set("bulk_delivery", v)}
                    />
                </div>
            </section>
        );
    }

    if (category === "paints") {
        return (
            <section data-testid="paints-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Paint specifications</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Painters and homeowners want clear type, finish and coverage info.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Paint type *</label>
                        <Select value={meta.paint_type || ""} onValueChange={(v) => set("paint_type", v)}>
                            <SelectTrigger data-testid="meta-paint-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Emulsion (Acrylic)", "Distemper", "Enamel", "Primer", "Wood Polish / Stain", "Texture / Designer", "Waterproofing", "Metallic", "Industrial / Epoxy"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Application</label>
                        <Select value={meta.application || ""} onValueChange={(v) => set("application", v)}>
                            <SelectTrigger data-testid="meta-application" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Interior Wall", "Exterior Wall", "Interior + Exterior", "Wood Surfaces", "Metal Surfaces", "Floor / Industrial"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Finish</label>
                        <Select value={meta.finish || ""} onValueChange={(v) => set("finish", v)}>
                            <SelectTrigger data-testid="meta-finish" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Matte", "Eggshell", "Satin", "Semi-Gloss", "High Gloss", "Textured"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Base</label>
                        <Select value={meta.base || ""} onValueChange={(v) => set("base", v)}>
                            <SelectTrigger data-testid="meta-base" className="mt-1"><SelectValue placeholder="Water / Oil" /></SelectTrigger>
                            <SelectContent>
                                {["Water-based", "Oil-based", "Solvent-based"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Volume per unit (litres)</label>
                        <Select value={meta.volume_litres || ""} onValueChange={(v) => set("volume_litres", v)}>
                            <SelectTrigger data-testid="meta-volume" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["1 L", "4 L", "5 L", "10 L", "20 L", "200 L (Drum)"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Coverage <span className="text-aduku-navy/50 font-normal">(sq ft / litre)</span></label>
                        <Input
                            data-testid="meta-coverage"
                            inputMode="numeric"
                            value={meta.coverage_sqft ?? ""}
                            onChange={(e) => set("coverage_sqft", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="e.g. 140"
                            className="mt-1"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Colour / shade card</label>
                    <Input
                        data-testid="meta-shade"
                        value={meta.shade || ""}
                        onChange={(e) => set("shade", e.target.value)}
                        placeholder="e.g. Ivory White (Asian Paints — 0123) / Custom mix"
                        maxLength={120}
                        className="mt-1"
                    />
                </div>
            </section>
        );
    }

    if (category === "building-materials") {
        return (
            <section data-testid="building-materials-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Material specifications</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Contractors filter on material type, unit and grade.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Material type *</label>
                        <Select value={meta.material_type || ""} onValueChange={(v) => set("material_type", v)}>
                            <SelectTrigger data-testid="meta-material-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Sand (M-sand / River)", "Bricks (Red / Fly-ash)", "TMT / Steel bars", "Tiles & Marble", "Granite", "Gravel / Aggregate", "Plywood / Wood", "Glass", "AAC Blocks", "Roofing Sheets", "PVC Pipes / Fittings", "Other"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Sale unit</label>
                        <Select value={meta.unit || ""} onValueChange={(v) => set("unit", v)}>
                            <SelectTrigger data-testid="meta-unit" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Per Tonne (Metric)", "Per Cubic Metre (m³)", "Per Truckload", "Per Piece", "Per Sq ft", "Per Bundle"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Grade / specification</label>
                        <Input
                            data-testid="meta-grade-spec"
                            value={meta.grade_spec || ""}
                            onChange={(e) => set("grade_spec", e.target.value)}
                            placeholder="e.g. Fe 500 / Class A bricks / Vitrified"
                            maxLength={80}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Min order quantity</label>
                        <Input
                            data-testid="meta-bm-min-qty"
                            inputMode="numeric"
                            value={meta.min_qty ?? ""}
                            onChange={(e) => set("min_qty", e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="e.g. 5"
                            className="mt-1"
                        />
                    </div>
                </div>
                <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-aduku-navy">Site delivery available</div>
                        <div className="text-xs text-aduku-navy/55">Direct truckload delivery to construction site.</div>
                    </div>
                    <Switch
                        data-testid="meta-bm-delivery"
                        checked={!!meta.site_delivery}
                        onCheckedChange={(v) => set("site_delivery", v)}
                    />
                </div>
            </section>
        );
    }

    if (category === "jewellery") {
        return (
            <section data-testid="jewellery-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Jewellery details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Buyers compare on metal, purity, weight and certification. Accurate info builds trust.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Jewellery type *</label>
                        <Select value={meta.jewellery_type || ""} onValueChange={(v) => set("jewellery_type", v)}>
                            <SelectTrigger data-testid="meta-jewellery-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Necklace", "Ring", "Earrings", "Bracelet", "Bangle", "Anklet", "Pendant", "Chain", "Mangalsutra", "Nose Pin / Ring", "Brooch", "Hair Accessories", "Tiara / Crown", "Cuff Links", "Watch", "Set (Necklace+Earrings)", "Bridal Set", "Coin / Bar"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Metal *</label>
                        <Select value={meta.metal || ""} onValueChange={(v) => set("metal", v)}>
                            <SelectTrigger data-testid="meta-metal" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Yellow Gold", "White Gold", "Rose Gold", "Silver", "Platinum", "Palladium", "Titanium", "Stainless Steel", "Brass / Alloy", "Imitation / Costume"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Purity / Karat</label>
                        <Select value={meta.purity || ""} onValueChange={(v) => set("purity", v)}>
                            <SelectTrigger data-testid="meta-purity" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["24K (999)", "22K (916)", "18K (750)", "14K (585)", "10K (417)", "9K (375)", "Silver 925 (Sterling)", "Silver 999 (Fine)", "Platinum 950 (PT950)", "Not applicable"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Gross weight (grams)</label>
                        <Input
                            data-testid="meta-weight"
                            inputMode="decimal"
                            value={meta.weight_g ?? ""}
                            onChange={(e) => set("weight_g", e.target.value.replace(/[^\d.]/g, "").slice(0, 8))}
                            placeholder="e.g. 12.5"
                            className="mt-1"
                        />
                        <div className="text-[11px] text-aduku-navy/50 mt-1">Total weight including stones &amp; setting.</div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Net metal weight (grams)</label>
                        <Input
                            data-testid="meta-net-weight"
                            inputMode="decimal"
                            value={meta.net_weight_g ?? ""}
                            onChange={(e) => set("net_weight_g", e.target.value.replace(/[^\d.]/g, "").slice(0, 8))}
                            placeholder="e.g. 10.2"
                            className="mt-1"
                        />
                        <div className="text-[11px] text-aduku-navy/50 mt-1">Excluding stones — what's billed at metal rate.</div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Hallmark / Certification</label>
                        <Select value={meta.hallmark || ""} onValueChange={(v) => set("hallmark", v)}>
                            <SelectTrigger data-testid="meta-hallmark" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["BIS Hallmark (with HUID)", "BIS Hallmark (legacy)", "Assay Office (UK / EU)", "SGL Certified", "GIA Certified", "IGI Certified", "GSI Certified", "Uncertified"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Gemstones / Diamonds <span className="text-aduku-navy/50 font-normal">(optional)</span></label>
                    <Input
                        data-testid="meta-gemstones"
                        value={meta.gemstones || ""}
                        onChange={(e) => set("gemstones", e.target.value)}
                        placeholder="e.g. Diamond 0.5ct (G/VS), Ruby 2pcs, Emerald, Pearl"
                        maxLength={200}
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Gender</label>
                        <Select value={meta.gender || ""} onValueChange={(v) => set("gender", v)}>
                            <SelectTrigger data-testid="meta-jewel-gender" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Women", "Men", "Unisex", "Kids", "Bridal"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Design style</label>
                        <Select value={meta.design_style || ""} onValueChange={(v) => set("design_style", v)}>
                            <SelectTrigger data-testid="meta-design-style" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Traditional / Temple", "Antique / Vintage", "Modern / Contemporary", "Minimalist", "Statement", "Kundan", "Polki", "Jadau", "Meenakari", "Filigree"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Occasion</label>
                        <Select value={meta.occasion || ""} onValueChange={(v) => set("occasion", v)}>
                            <SelectTrigger data-testid="meta-occasion" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Daily Wear", "Office / Casual", "Party / Cocktail", "Wedding / Bridal", "Festive", "Gifting", "Investment"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Making charges <span className="text-aduku-navy/50 font-normal">(optional)</span></label>
                    <Input
                        data-testid="meta-making-charges"
                        value={meta.making_charges || ""}
                        onChange={(e) => set("making_charges", e.target.value)}
                        placeholder="e.g. ₹450/g flat OR 12% of metal value"
                        maxLength={80}
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">Lifetime buy-back / exchange</div>
                            <div className="text-xs text-aduku-navy/55">Boosts buyer confidence on gold purchases.</div>
                        </div>
                        <Switch
                            data-testid="meta-buyback"
                            checked={!!meta.lifetime_buyback}
                            onCheckedChange={(v) => set("lifetime_buyback", v)}
                        />
                    </div>
                    <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">Insurance / shipping included</div>
                            <div className="text-xs text-aduku-navy/55">Insured shipping included with the order.</div>
                        </div>
                        <Switch
                            data-testid="meta-insured-shipping"
                            checked={!!meta.insured_shipping}
                            onCheckedChange={(v) => set("insured_shipping", v)}
                        />
                    </div>
                </div>
            </section>
        );
    }

    if (category === "real-estate") {
        // Toggle bedrooms/bathrooms only for residential property types.
        const isResidential = ["Apartment / Flat", "Independent House / Villa", "Builder Floor", "Penthouse", "Studio Apartment", "PG / Hostel", "Farmhouse", "Service Apartment"].includes(meta.property_type);
        return (
            <section data-testid="real-estate-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Property details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Buyers and renters filter on property type, listing type, BHK and area. Accurate info attracts the right enquiries.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    {/* PRIMARY: Property type */}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Property type *</label>
                        <Select value={meta.property_type || ""} onValueChange={(v) => set("property_type", v)}>
                            <SelectTrigger data-testid="meta-property-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent className="max-h-72">
                                {[
                                    "Apartment / Flat",
                                    "Independent House / Villa",
                                    "Builder Floor",
                                    "Penthouse",
                                    "Studio Apartment",
                                    "PG / Hostel",
                                    "Farmhouse",
                                    "Service Apartment",
                                    "Plot / Land (Residential)",
                                    "Plot / Land (Commercial)",
                                    "Agricultural / Farm Land",
                                    "Commercial Office Space",
                                    "Co-working Seat / Cabin",
                                    "Retail Store / Showroom",
                                    "Warehouse / Godown",
                                    "Industrial Shed / Factory",
                                    "Hotel / Resort",
                                    "Banquet / Function Hall",
                                    "Parking Slot / Garage",
                                    "Other",
                                ].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* PRIMARY: Listing type (For Sale / Rent / Lease / PG / Short-stay) */}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Listing type *</label>
                        <Select value={meta.listing_type || ""} onValueChange={(v) => set("listing_type", v)}>
                            <SelectTrigger data-testid="meta-listing-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {[
                                    "For Sale",
                                    "For Rent",
                                    "For Lease",
                                    "PG / Co-living",
                                    "Short-stay / Holiday",
                                    "Rent-to-Own",
                                    "Auction",
                                    "Wanted (Looking to buy/rent)",
                                ].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* BHK (residential only) */}
                    {isResidential && (
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">BHK / Bedrooms</label>
                            <Select value={meta.bhk || ""} onValueChange={(v) => set("bhk", v)}>
                                <SelectTrigger data-testid="meta-bhk" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    {["1 RK", "1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK", "6+ BHK"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Bathrooms (residential only) */}
                    {isResidential && (
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">Bathrooms</label>
                            <Select value={meta.bathrooms || ""} onValueChange={(v) => set("bathrooms", v)}>
                                <SelectTrigger data-testid="meta-bathrooms" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    {["1", "2", "3", "4", "5+"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Area */}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Built-up area</label>
                        <Input
                            data-testid="meta-area-value"
                            inputMode="numeric"
                            value={meta.area_value ?? ""}
                            onChange={(e) => set("area_value", e.target.value.replace(/[^\d.]/g, "").slice(0, 8))}
                            placeholder="e.g. 1250"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Area unit</label>
                        <Select value={meta.area_unit || ""} onValueChange={(v) => set("area_unit", v)}>
                            <SelectTrigger data-testid="meta-area-unit" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["sq ft", "sq m", "sq yd", "acres", "hectares", "cents", "guntha", "marla", "kanal", "bigha"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Furnishing — residential only */}
                    {isResidential && (
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">Furnishing</label>
                            <Select value={meta.furnishing || ""} onValueChange={(v) => set("furnishing", v)}>
                                <SelectTrigger data-testid="meta-furnishing" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    {["Unfurnished", "Semi-furnished", "Fully furnished"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Possession status */}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Possession status</label>
                        <Select value={meta.possession || ""} onValueChange={(v) => set("possession", v)}>
                            <SelectTrigger data-testid="meta-possession" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Ready to move", "Under construction", "New launch", "Resale", "Within 3 months", "Within 6 months", "Within 1 year"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Age — residential / commercial built */}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Age of property</label>
                        <Select value={meta.age || ""} onValueChange={(v) => set("age", v)}>
                            <SelectTrigger data-testid="meta-age" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Under construction", "Brand new", "Less than 1 year", "1-5 years", "5-10 years", "10-20 years", "20+ years"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Floor (residential / commercial only) */}
                    {isResidential && (
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">Floor <span className="text-aduku-navy/50 font-normal">(of total)</span></label>
                            <Input
                                data-testid="meta-floor"
                                value={meta.floor || ""}
                                onChange={(e) => set("floor", e.target.value)}
                                placeholder="e.g. 4 / 12"
                                maxLength={20}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {/* Facing */}
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Facing</label>
                        <Select value={meta.facing || ""} onValueChange={(v) => set("facing", v)}>
                            <SelectTrigger data-testid="meta-facing" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["North", "East", "South", "West", "North-East", "North-West", "South-East", "South-West"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Rent-only details — shown when listing_type implies tenancy */}
                {["For Rent", "For Lease", "PG / Co-living", "Short-stay / Holiday"].includes(meta.listing_type) && (
                    <div className="grid md:grid-cols-3 gap-3 bg-white border border-aduku-border rounded-lg p-3">
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">Security deposit</label>
                            <Input
                                data-testid="meta-deposit"
                                value={meta.deposit || ""}
                                onChange={(e) => set("deposit", e.target.value)}
                                placeholder="e.g. 2 months / 50,000"
                                maxLength={40}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">Maintenance / mo</label>
                            <Input
                                data-testid="meta-maintenance"
                                value={meta.maintenance || ""}
                                onChange={(e) => set("maintenance", e.target.value)}
                                placeholder="e.g. ₹2,500 / mo"
                                maxLength={40}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-aduku-navy">Preferred tenants</label>
                            <Select value={meta.tenants || ""} onValueChange={(v) => set("tenants", v)}>
                                <SelectTrigger data-testid="meta-tenants" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                                <SelectContent>
                                    {["Family", "Bachelors (Male)", "Bachelors (Female)", "Working professionals", "Students", "Company / Corporate", "Anyone"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {/* Amenities — free text, comma-separated */}
                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Amenities</label>
                    <Textarea
                        data-testid="meta-amenities"
                        rows={2}
                        value={meta.amenities || ""}
                        onChange={(e) => set("amenities", e.target.value)}
                        maxLength={400}
                        placeholder="e.g. Lift, Gym, Swimming pool, Covered parking, Power backup, Pet-friendly"
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">Parking available</div>
                            <div className="text-xs text-aduku-navy/55">Covered, open or basement parking.</div>
                        </div>
                        <Switch
                            data-testid="meta-parking"
                            checked={!!meta.parking}
                            onCheckedChange={(v) => set("parking", v)}
                        />
                    </div>
                    <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">RERA / approved project</div>
                            <div className="text-xs text-aduku-navy/55">Boosts buyer trust on new builds.</div>
                        </div>
                        <Switch
                            data-testid="meta-rera"
                            checked={!!meta.rera_approved}
                            onCheckedChange={(v) => set("rera_approved", v)}
                        />
                    </div>
                </div>
            </section>
        );
    }

    return null;
}
